import type { FormField, FormWidget, OtpWidget, ShareWidget, UserFilesContent, WidgetSpec } from '@oneapp/shared';
import { TEST_PORTAL_CODE } from '@oneapp/shared';
import type { TFunction } from 'i18next';
import { type ReactNode, useEffect, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CopyButton } from '../components/CopyButton';
import { OtpInput } from '../components/forms';
import { Icon } from '../components/icons';
import { Sheet } from '../components/Sheet';
import { Button, Callout } from '../components/ui';
import { Gstr3bSheet, isGstr3bPath } from '../gstr3b/Gstr3bSheet';
import { CheckSheet } from '../imports/CheckSheet';
import { FixRowSheet } from '../imports/FixRow';
import { PhotoSheet } from '../imports/PhotoSheet';
import { ReviewSheet } from '../imports/ReviewSheet';
import { UploadSheet } from '../imports/UploadSheet';
import { parseStatementPath, parseWalkInPath } from '../records/api';
import { PaymentsSheet } from '../records/PaymentsSheet';
import { StatementSheet } from '../records/StatementSheet';
import { WalkInSheet } from '../records/WalkInScreen';
import { SalesSheet } from '../sales/SalesScreens';
import { safeUrl } from './markdown';
import { PaymentSheet } from './PaymentSheet';

export type WidgetData = Record<string, string | number | boolean>;

/**
 * A widget opened from the chat as an in-chat page (a sheet): a one-time code, a form, a file upload,
 * one of the app's own pages, or a challan to pay at the bank (Phase 11). Embedded pages arrive with
 * the phases that use them. Nothing typed here is kept after it's sent.
 */
export function WidgetSheet({
  widget,
  codeError,
  onClose,
  onSubmit,
  onAction,
}: {
  widget: WidgetSpec | null;
  codeError?: string | undefined;
  onClose: () => void;
  /** What was entered; `echo` is how it shows as the person's message (a file, say). */
  onSubmit: (data: WidgetData, echo?: UserFilesContent) => void;
  onAction: (action: string) => void;
}) {
  const { t } = useTranslation();
  const formId = useId();
  const [ready, setReady] = useState(false);
  if (!widget) return null;
  // One of the app's own pages (the month's sales, a row of a file): coming back tells the chat what happened.
  if (widget.kind === 'page') {
    const review = /^\/imports\/([\w-]+)\/review$/.exec(widget.path);
    if (review?.[1]) return <ReviewSheet inChat importId={review[1]} onClose={onClose} onFinish={onAction} />;
    if (isGstr3bPath(widget.path)) {
      return <Gstr3bSheet path={widget.path} onClose={onClose} onFinish={(how) => onAction(how)} />;
    }
    if (/^\/imports\/[\w-]+\/checks\/?$/.test(widget.path)) {
      return <CheckSheet path={widget.path} onClose={onClose} onFinish={(how) => onAction(how)} />;
    }
    // Phase 16: a statement's every payment (R7), and walk-in sales by rate (R11).
    if (parseStatementPath(widget.path)) {
      return <PaymentsSheet path={widget.path} onClose={onClose} onFinish={(how) => onAction(how)} />;
    }
    if (parseWalkInPath(widget.path)) {
      return <WalkInSheet path={widget.path} onClose={onClose} onFinish={(how) => onAction(how)} />;
    }
    if (widget.path.startsWith('/imports/')) {
      return <FixRowSheet path={widget.path} onClose={onClose} onFinish={(how) => onAction(how)} />;
    }
    return <SalesSheet path={widget.path} onClose={onClose} onFinish={(how) => onAction(how)} />;
  }
  // Photos of invoices: they go up from their sheet and come back as the person's message.
  if (widget.kind === 'upload' && widget.purpose === 'invoice_photo') {
    return (
      <PhotoSheet
        widget={widget}
        onClose={onClose}
        onUploaded={(uploadIds, files) => onSubmit({ uploadIds }, { type: 'user_files', files })}
      />
    );
  }
  // A bank or UPI statement (R5): it goes up from its page and comes back as the person's message.
  if (widget.kind === 'upload' && widget.purpose === 'bank_statement') {
    return (
      <StatementSheet
        widget={widget}
        onClose={onClose}
        onUploaded={(upload) =>
          onSubmit(
            { uploadId: upload.id },
            {
              type: 'user_files',
              files: [{ id: upload.id, name: upload.fileName, size: upload.size, mime: upload.contentType }],
            },
          )
        }
      />
    );
  }
  // Gap 4b: a message for the person to send (a rejected credit note, for the buyer): nothing comes back.
  if (widget.kind === 'share') return <ShareSheet widget={widget} onClose={onClose} />;
  // A challan to pay at the bank (S15): "I've paid" comes back as the person's message.
  if (widget.kind === 'payment') {
    return <PaymentSheet widget={widget} onClose={onClose} onPaid={() => onSubmit({})} />;
  }
  // A file: it goes up from the sheet and comes back as the person's message.
  if (widget.kind === 'upload') {
    return (
      <UploadSheet
        widget={widget}
        onClose={onClose}
        onUploaded={(upload) =>
          onSubmit(
            { uploadId: upload.id },
            {
              type: 'user_files',
              files: [{ id: upload.id, name: upload.fileName, size: upload.size, mime: upload.contentType }],
            },
          )
        }
      />
    );
  }

  const submitLabel =
    widget.kind === 'form'
      ? widget.submitLabel
      : (widget.kind === 'otp' && widget.submitLabel) || t('chat.widget.submit');
  const canSubmit = widget.kind === 'form' || (widget.kind === 'otp' && ready);
  return (
    <Sheet
      open
      onClose={onClose}
      label={widget.secureLabel ?? t('chat.widget.secure')}
      secure
      footer={
        widget.kind === 'otp' || widget.kind === 'form' ? (
          <Button type="submit" form={formId} disabled={!canSubmit}>
            {submitLabel}
          </Button>
        ) : null
      }
    >
      {codeError ? (
        <p role="alert" className="field__help field__help--error">
          {codeError}
        </p>
      ) : null}
      <h2 className="sheet__title">{widget.title}</h2>
      {widget.description ? <p className="sheet__text">{widget.description}</p> : null}
      {widget.note ? <Callout>{widget.note}</Callout> : null}
      {widget.kind === 'otp' ? (
        <OtpBody widget={widget} formId={formId} onReady={setReady} onSubmit={onSubmit} onAction={onAction} />
      ) : widget.kind === 'form' ? (
        <FormBody widget={widget} formId={formId} onSubmit={onSubmit} />
      ) : (
        <Callout icon="clock">{t('chat.widget.soon')}</Callout>
      )}
      {widget.links?.length ? (
        <ul className="plain-list">
          {widget.links.map((link) => {
            const url = safeUrl(link.url);
            return url ? (
              <li key={link.url}>
                <a href={url} target="_blank" rel="noopener noreferrer">
                  {link.label}
                </a>
              </li>
            ) : null;
          })}
        </ul>
      ) : null}
    </Sheet>
  );
}

function OtpBody({
  widget,
  formId,
  onReady,
  onSubmit,
  onAction,
}: {
  widget: OtpWidget;
  formId: string;
  onReady: (ready: boolean) => void;
  onSubmit: (data: WidgetData) => void;
  onAction: (action: string) => void;
}) {
  const { t } = useTranslation();
  const [code, setCode] = useState('');
  const [wait, setWait] = useState(widget.resendAfterSec ?? 0);
  useEffect(() => {
    if (wait <= 0) return;
    const timer = setTimeout(() => setWait((s) => s - 1), 1000);
    return () => clearTimeout(timer);
  }, [wait]);
  useEffect(() => onReady(code.length === widget.length), [code, widget.length, onReady]);
  const send = (value: string) => {
    if (value.length === widget.length) {
      onSubmit({ otp: value });
      setCode('');
    }
  };
  return (
    <form
      id={formId}
      className="wform"
      onSubmit={(event) => {
        event.preventDefault();
        send(code);
      }}
    >
      {widget.purpose ? <p className="sheet__text">{widget.purpose}</p> : null}
      {widget.signer ? (
        // EVC: who signs, and where their code went.
        <div className="signer">
          <span className="icon-tile">
            <Icon name="user" />
          </span>
          <span className="signer__text">
            <span className="signer__name">{widget.signer.name}</span>
            <span className="signer__detail">{widget.signer.detail}</span>
            {widget.sentTo.length ? (
              <span className="signer__sent">{t('chat.widget.sentTo', { to: widget.sentTo.join(', ') })}</span>
            ) : null}
          </span>
        </div>
      ) : widget.sentTo.length ? (
        <p className="sheet__text muted">{t('chat.widget.sentTo', { to: widget.sentTo.join(', ') })}</p>
      ) : null}
      <OtpInput
        length={widget.length}
        letters={widget.letters === true}
        value={code}
        onChange={setCode}
        onComplete={send}
        label={t(widget.letters ? 'chat.widget.codeLabelLetters' : 'chat.widget.codeLabel', { length: widget.length })}
      />
      {widget.attemptsLeft !== undefined ? (
        <p className="sheet__text muted">{t('chat.widget.attemptsLeft', { count: widget.attemptsLeft })}</p>
      ) : null}
      {widget.hint ? (
        <Callout tone="amber" icon="flask">
          {widget.hint}
        </Callout>
      ) : null}
      {widget.testHint ? (
        <Callout tone="amber" icon="flask">
          {t(widget.testHint === 'otp' ? 'chat.widget.otpTestHint' : 'chat.widget.evcTestHint', {
            otp: TEST_PORTAL_CODE,
          })}
        </Callout>
      ) : null}
      {widget.resendAfterSec !== undefined ? (
        <Button
          variant="ghost"
          inline
          disabled={wait > 0}
          onClick={() => {
            onAction('resend');
            setWait(widget.resendAfterSec ?? 0);
          }}
        >
          {wait > 0 ? t('chat.widget.resendIn', { seconds: wait }) : t('chat.widget.resend')}
        </Button>
      ) : null}
    </form>
  );
}

// ---------------------------------------------------------------------------
// Forms
// ---------------------------------------------------------------------------

type Values = Record<string, string | boolean>;

function initialValues(widget: FormWidget): Values {
  const values: Values = {};
  for (const field of widget.fields) {
    const given = widget.values?.[field.name] ?? field.defaultValue;
    values[field.name] = field.type === 'checkbox' ? given === true : given === undefined ? '' : String(given);
  }
  return values;
}

/** Fields shown only when another field has a given value. */
function shows(field: FormField, values: Values): boolean {
  if (!field.showIf) return true;
  const wanted = Array.isArray(field.showIf.equals) ? field.showIf.equals : [field.showIf.equals];
  return wanted.includes(values[field.showIf.field] ?? '');
}

const NUMERIC = new Set(['number', 'currency']);

/** The same checks the server makes, so mistakes show before sending. */
export function checkField(field: FormField, value: string | boolean, t: TFunction): string | null {
  if (field.type === 'checkbox') return field.required && value !== true ? t('chat.widget.required') : null;
  const text = String(value).trim();
  if (!text) return field.required ? t('chat.widget.required') : null;
  if (field.maxLength !== undefined && text.length > field.maxLength) return t('chat.widget.invalid');
  if (field.pattern && !new RegExp(`^(?:${field.pattern})$`, 'u').test(text))
    return field.patternMessage ?? t('chat.widget.invalid');
  if (NUMERIC.has(field.type)) {
    const number = Number(text.replace(/,/g, ''));
    if (!Number.isFinite(number)) return t('chat.widget.invalid');
    if (typeof field.min === 'number' && number < field.min) return t('chat.widget.min', { min: field.min });
    if (typeof field.max === 'number' && number > field.max) return t('chat.widget.max', { max: field.max });
  }
  if ((field.type === 'date' || field.type === 'month') && typeof field.min === 'string' && text < field.min)
    return t('chat.widget.min', { min: field.min });
  if ((field.type === 'date' || field.type === 'month') && typeof field.max === 'string' && text > field.max)
    return t('chat.widget.max', { max: field.max });
  return null;
}

function FormBody({
  widget,
  formId,
  onSubmit,
}: {
  widget: FormWidget;
  formId: string;
  onSubmit: (data: WidgetData) => void;
}) {
  const { t } = useTranslation();
  const [values, setValues] = useState<Values>(() => initialValues(widget));
  const [errors, setErrors] = useState<Record<string, string>>({});
  const visible = widget.fields.filter((field) => shows(field, values));

  const submit = () => {
    const found: Record<string, string> = {};
    for (const field of visible) {
      const error = checkField(field, values[field.name] ?? '', t);
      if (error) found[field.name] = error;
    }
    setErrors(found);
    if (Object.keys(found).length) return;
    const data: WidgetData = {};
    for (const field of visible) {
      const value = values[field.name] ?? '';
      if (typeof value === 'boolean') data[field.name] = value;
      else if (NUMERIC.has(field.type) && value.trim()) data[field.name] = Number(value.replace(/,/g, ''));
      else data[field.name] = field.type === 'password' ? value : value.trim();
    }
    onSubmit(data);
    if (widget.fields.some((field) => field.type === 'password' || field.sensitive))
      setValues(initialValues({ ...widget, values: {} }));
  };

  return (
    <form
      id={formId}
      className="wform"
      noValidate
      onSubmit={(event) => {
        event.preventDefault();
        submit();
      }}
    >
      {visible.map((field) => (
        <FormFieldView
          key={field.name}
          field={field}
          value={values[field.name] ?? ''}
          error={errors[field.name]}
          onChange={(value) => setValues((current) => ({ ...current, [field.name]: value }))}
        />
      ))}
    </form>
  );
}

const INPUT_TYPE: Partial<Record<FormField['type'], string>> = {
  email: 'email',
  tel: 'tel',
  password: 'password',
  date: 'date',
  month: 'month',
};

function FormFieldView({
  field,
  value,
  error,
  onChange,
}: {
  field: FormField;
  value: string | boolean;
  error: string | undefined;
  onChange: (value: string | boolean) => void;
}) {
  const id = useId();
  const helpId = `${id}-help`;
  const message = error ?? field.help;
  const described = message ? helpId : undefined;
  const help = message ? (
    <p id={helpId} className={`field__help${error ? ' field__help--error' : ''}`}>
      {message}
    </p>
  ) : null;

  if (field.type === 'checkbox') {
    return (
      <div className="field">
        <label className={`consent${error ? ' consent--error' : ''}`} htmlFor={id}>
          <input
            id={id}
            type="checkbox"
            checked={value === true}
            onChange={(event) => onChange(event.target.checked)}
            aria-invalid={error ? true : undefined}
            aria-describedby={described}
          />
          <span>{field.label}</span>
        </label>
        {help}
      </div>
    );
  }

  if (field.type === 'radio') {
    return (
      <fieldset className="field wform__radios" aria-describedby={described}>
        <legend className="field__label">{field.label}</legend>
        {(field.options ?? []).map((option) => (
          <label key={option.value} className="wform__radio">
            <input
              type="radio"
              name={field.name}
              value={option.value}
              checked={value === option.value}
              onChange={() => onChange(option.value)}
            />
            {option.label}
          </label>
        ))}
        {help}
      </fieldset>
    );
  }

  const common = {
    id,
    value: String(value),
    'aria-invalid': error ? true : undefined,
    'aria-describedby': described,
    placeholder: field.placeholder,
  } as const;
  let control: ReactNode;
  if (field.type === 'select') {
    control = (
      <select {...common} className="input select" onChange={(event) => onChange(event.target.value)}>
        <option value="" disabled>
          {field.placeholder ?? ''}
        </option>
        {(field.options ?? []).map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    );
  } else if (field.type === 'textarea') {
    control = (
      <textarea
        {...common}
        className="input input--area"
        maxLength={field.maxLength}
        onChange={(event) => onChange(event.target.value)}
      />
    );
  } else {
    const upper = field.uppercase || field.type === 'gstin';
    const input = (
      <input
        {...common}
        className={`input${field.type === 'gstin' ? ' input--mono' : ''}`}
        type={INPUT_TYPE[field.type] ?? 'text'}
        inputMode={NUMERIC.has(field.type) ? 'decimal' : field.type === 'tel' ? 'tel' : undefined}
        maxLength={field.maxLength ?? (field.type === 'gstin' ? 15 : undefined)}
        min={typeof field.min === 'string' ? field.min : undefined}
        max={typeof field.max === 'string' ? field.max : undefined}
        autoComplete={field.sensitive ? 'off' : undefined}
        spellCheck={false}
        onChange={(event) => onChange(upper ? event.target.value.toUpperCase() : event.target.value)}
      />
    );
    control = field.type === 'currency' ? <div className="money">{input}</div> : input;
  }
  return (
    <div className="field">
      <label className="field__label" htmlFor={id}>
        {field.label}
      </label>
      {control}
      {help}
    </div>
  );
}

/** A message to send someone, shown as it'll go: Share opens the phone's share sheet, or copies it. */
function ShareSheet({ widget, onClose }: { widget: ShareWidget; onClose: () => void }) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const share = async () => {
    try {
      if (navigator.share) {
        await navigator.share({ text: widget.text });
        return;
      }
    } catch {
      // Closed without sharing: the message is still there to copy.
      return;
    }
    await navigator.clipboard
      ?.writeText(widget.text)
      .then(() => setCopied(true))
      .catch(() => {});
  };
  return (
    <Sheet
      open
      onClose={onClose}
      label={widget.title}
      footer={
        <Button icon="share" onClick={() => void share()}>
          {widget.cta}
        </Button>
      }
    >
      <h2 className="sheet__title">{widget.title}</h2>
      {widget.description ? <p className="sheet__text">{widget.description}</p> : null}
      <div className="share-text">
        <p className="share-text__body">{widget.text}</p>
        <CopyButton value={widget.text} label={widget.title} />
      </div>
      {copied ? (
        <p className="field__help field__help--ok" role="status">
          {t('chat.widget.shareCopied')}
        </p>
      ) : null}
    </Sheet>
  );
}
